import { TFunction } from 'react-i18next';

import { cfvChangeTypeOptionsConst } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/common';
import DraggableStepCard from '__pages/AutomationEngine/Engine/Steps/DraggableStepCard';
import { KizenTypography } from '__app/typography';

import { emptyLongDash } from 'components/Inputs/DateTimeInput/helpers';
import {
  getFieldValue,
  getValueOrDeleted,
  getLabelWithDeleted,
  getNameWithDeleted,
} from './action-helpers';
import { usingRelatedObject } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/common';

import {
  type Field,
  type Automation,
  type Step,
} from 'pages/AutomationEngine/types';
import { type CardProps } from '../../types';
import { type FieldsToModifyValues } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/fieldsToModifyContext';
import { addDeleted } from 'pages/AutomationEngine/dialogs/common';

export const content = ({ step }: { step: Step }) => step.description;

type GetDescription = {
  step: Step & {
    details: FieldsToModifyValues;
  };
  automation: Automation;
  fields: Array<Field>;

  t: TFunction;
};

const MAX_ACTIONS_FOR_FULL_DISPLAY = 4;

export const getMultiValue = (value: any[], t: TFunction) => {
  if (!Array.isArray(value) || value.length === 0) {
    return t('Clear Field Value');
  }

  const options = value.map((val) => `‘${getLabelWithDeleted(val, t)}’`);

  if (value.length === 1) {
    return t('Clear Field {{option}}', {
      option: options[0],
    });
  }

  if (value.length <= 3) {
    const lastOption = options.pop();
    return t('Clear Fields {{options}}, and {{lastOption}}', {
      options: options.join(', '),
      lastOption,
    });
  }

  return t('Clear Fields {{options}}, and {{count}} more', {
    options: options.slice(0, 2).join(', '),
    count: options.length - 2,
  });
};

export const getDescription = ({ step, t }: GetDescription) => {
  const { details } = step;

  const actionsLength = details.actions?.length || 0;

  if (actionsLength === 0) {
    return t('Change Field');
  }

  const updateMode = details.actions[0].updateMode;

  if (updateMode === 'clear_fields') {
    return getMultiValue(details.actions[0]?.fieldsToClear, t);
  }

  const numberOfDetailsToShow =
    actionsLength < MAX_ACTIONS_FOR_FULL_DISPLAY ? actionsLength : 2;
  const additionalActionsLength = actionsLength - numberOfDetailsToShow;

  const detailsToShowList = details.actions.slice(0, numberOfDetailsToShow);

  const detailsToShow = detailsToShowList.map((field) => {
    const { changeType } = field;
    const changeTypeValue =
      typeof changeType === 'string' ? changeType : changeType?.value;

    const fieldLabel = getNameWithDeleted(field, t);

    if (changeTypeValue === cfvChangeTypeOptionsConst.SPECIFIC_VALUE && field) {
      return t('‘{{fieldLabel}}’ to ‘{{valueLabel}}’', {
        fieldLabel,
        valueLabel: getFieldValue(field, t) || emptyLongDash,
      });
    }

    if (changeTypeValue === cfvChangeTypeOptionsConst.BLANK_VALUE) {
      return t('‘{{fieldLabel}}’ to ‘Blank Value’', {
        fieldLabel,
      });
    }

    if (changeTypeValue === cfvChangeTypeOptionsConst.AUTOMATION_VARIABLE) {
      return t('‘{{fieldLabel}}’ to Variable ‘{{valueLabel}}’', {
        fieldLabel,
        valueLabel:
          getValueOrDeleted(field?.variable?.label, t) || emptyLongDash,
      });
    }

    const {
      relatedObject,
      relatedObjectField: _relatedObjectField,
      thisRecordField,
    } = field;

    const relatedObjectField = usingRelatedObject(changeTypeValue)
      ? _relatedObjectField
      : thisRecordField;

    if (changeTypeValue === cfvChangeTypeOptionsConst.ENTITY_VARIABLE_FIELD) {
      return t(
        '‘{{fieldLabel}}’ to Value of ‘{{valueLabel}}: {{relatedObjectField}}’',
        {
          fieldLabel: getNameWithDeleted(field, t),
          valueLabel: getValueOrDeleted(
            field?.variable?.meta?.dataSubtype?.label,
            t
          ),
          relatedObjectField: relatedObjectField
            ? addDeleted(relatedObjectField, t)?.label
            : t('Deleted'),
        }
      );
    }

    return t(
      '‘{{fieldLabel}}’ to Value of ‘{{valueLabel}}: {{relatedObjectField}}’',
      {
        fieldLabel: getNameWithDeleted(field, t),
        valueLabel: getValueOrDeleted(relatedObject?.label, t),
        relatedObjectField: relatedObjectField
          ? addDeleted(relatedObjectField, t)?.label
          : t('Deleted'),
      }
    );
  });

  if (actionsLength === 1) {
    return t('Change Field {{details}}', {
      details: detailsToShow[0],
    });
  }

  if (actionsLength < MAX_ACTIONS_FOR_FULL_DISPLAY) {
    const lastOption = detailsToShow.pop();
    return t('Change Fields {{details}}, and {{lastOption}}', {
      details: detailsToShow.join(', ').trim(),
      lastOption,
    });
  }

  return t(
    'Change Fields {{details}}, and {{additionalActionsLength}} Additional Field Updates',
    { details: detailsToShow.join(', ').trim(), additionalActionsLength }
  );
};

export default function ChangeFieldValueCard({ step, ...others }: CardProps) {
  return (
    <DraggableStepCard {...others}>
      <KizenTypography as="span">{content({ step })}</KizenTypography>
    </DraggableStepCard>
  );
}
